import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { FeatureFlagProvider, useFeatureFlags } from './context/FeatureFlagContext';
import { Toaster } from './components/ui/sonner';
import FloatingLabBack from './components/FloatingLabBack';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AuthCallback from './pages/AuthCallback';
import Dashboard from './pages/Dashboard';
import CourseBrowser from './pages/CourseBrowser';
import CodeLab from './pages/CodeLab';
import MissionRunner from './pages/MissionRunner';
import ExamPage from './pages/ExamPage';
import AdminDashboard from './pages/AdminDashboard';
import ProfilePage from './pages/ProfilePage';
import Gamification from './pages/Gamification';
import Explorer from './pages/Explorer';
import StudentPerformance from './pages/StudentPerformance';
import VisualLabs from './pages/VisualLabs';
import PhysicsLab from './pages/labs/PhysicsLab';
import ChemistryLab from './pages/labs/ChemistryLab';
import SortingLab from './pages/labs/SortingLab';
import DroneLab from './pages/labs/DroneLab';
import GeometryLab from './pages/labs/GeometryLab';
import AIPromptLab from './pages/labs/AIPromptLab';
import ExamModule from './pages/ExamModule';
import PaperScanPage from './pages/PaperScanPage';
import StorytellerPage from './pages/StorytellerPage';
import StoryTheatrePage from './pages/StoryTheatrePage';
import CompetitiveExams from './pages/CompetitiveExams';
import BoardExamSimulator from './pages/BoardExamSimulator';
import AlgebraLab from './pages/labs/AlgebraLab';
import HistoryLab from './pages/labs/HistoryLab';
import GeographyLab from './pages/labs/GeographyLab';
import ElectricalLab from './pages/labs/ElectricalLab';
import InteractiveLab from './pages/labs/InteractiveLab';
import GameLabRouter from './pages/labs/GameLabRouter';
import VisualLabRouter from './pages/labs/VisualLabRouter';
import LabProgressPage from './pages/LabProgressPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import ChemReactionSimLab from './pages/labs/ChemReactionSimLab';
import VirtualDissectionLab from './pages/labs/VirtualDissectionLab';
import PhysicsSimLab from './pages/labs/PhysicsSimLab';
import EarthExplorerLab from './pages/labs/EarthExplorerLab';
import MathVisualizerLab from './pages/labs/MathVisualizerLab';
import EngineeringSimLab from './pages/labs/EngineeringSimLab';
import EngineeringDesignerLab from './pages/labs/EngineeringDesignerLab';
import InstallTutorialPage from './pages/labs/InstallTutorialPage';
import EngineeringPlaygroundPage from './pages/labs/EngineeringPlaygroundPage';
import OnboardingPage from './pages/OnboardingPage';
import SubscriptionPage from './pages/SubscriptionPage';
import BillingPage from './pages/BillingPage';
import PaymentHistoryPage from './pages/PaymentHistoryPage';
import BodyExplorerLab from './pages/labs/BodyExplorerLab';
import CityBuilderLab from './pages/labs/CityBuilderLab';
import FinanceSimLab from './pages/labs/FinanceSimLab';
import HistoryExplorerLab from './pages/labs/HistoryExplorerLab';
import ClassroomHub from './pages/classroom/ClassroomHub';
import ClassDetail from './pages/classroom/ClassDetail';
import AssignmentView from './pages/classroom/AssignmentView';
import DocsHub from './pages/suite/DocsHub';
import DocEditor from './pages/suite/DocEditor';
import DrivePage from './pages/suite/DrivePage';
import CalendarPage from './pages/suite/CalendarPage';
import ChatPage from './pages/suite/ChatPage';
import EmailPage from './pages/suite/EmailPage';
import MeetPage from './pages/suite/MeetPage';
import SheetsHub from './pages/suite/SheetsHub';
import SheetEditor from './pages/suite/SheetEditor';
import SlidesHub from './pages/suite/SlidesHub';
import SlideEditor from './pages/suite/SlideEditor';
import WhiteboardPage from './pages/suite/WhiteboardPage';
import SuiteHub from './pages/suite/SuiteHub';
import ParentDashboard from './pages/ParentDashboard';
import ErrorBook from './pages/ErrorBook';
import PerformanceHub from './pages/PerformanceHub';
import ExamLab from './pages/ExamLab';
import MockTest from './pages/MockTest';
import DailyPractice from './pages/DailyPractice';
import RevisionHub from './pages/RevisionHub';
import RankPredictor from './pages/RankPredictor';
import AITSHub from './pages/AITSHub';
import AdminAITS from './pages/AdminAITS';
import StudyPlan from './pages/StudyPlan';
import StudyMaterialHub from './pages/StudyMaterialHub';
import StudyMaterialSubjects from './pages/StudyMaterialSubjects';
import StudyMaterialTopics from './pages/StudyMaterialTopics';
import StudyMaterialViewer from './pages/StudyMaterialViewer';
import LiveClassroom from './pages/LiveClassroom';
import BotExamDemo from './pages/BotExamDemo';

import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = null }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Parents are restricted to the Parent Dashboard only — they should never
  // see learning content (courses, labs, exams). If a parent lands on any
  // route that isn't `/parent`, send them home immediately.
  if (user?.role === 'parent' && !location.pathname.startsWith('/parent')) {
    return <Navigate to="/parent" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to={user?.role === 'parent' ? '/parent' : '/dashboard'} replace />;
  }

  // Phase 40.13 — students who never finished the questionnaire get pushed
  // through it before they can use any protected page.  Skip the guard on
  // /onboarding itself so we don't loop.  A missing `onboarded` flag on
  // legacy accounts is treated the same as `false` — they will fill the form
  // once and then continue as normal.
  const STUDENT_ROLES = ['student', 'independent_student'];
  if (
    user &&
    STUDENT_ROLES.includes(user.role) &&
    !user.onboarded &&
    !location.pathname.startsWith('/onboarding')
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return children;
};

// Feature-flag-gated Route
const FlaggedRoute = ({ flagId, children }) => {
  const { isEnabled } = useFeatureFlags();
  if (!isEnabled(flagId)) return <Navigate to="/dashboard" replace />;
  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id in URL fragment (Google OAuth callback)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/payment-history" element={<ProtectedRoute><PaymentHistoryPage /></ProtectedRoute>} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* Protected Routes - All Users */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/courses" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_courses">
            <CourseBrowser />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/lab/:courseId" element={
        <ProtectedRoute>
          <CodeLab />
        </ProtectedRoute>
      } />
      <Route path="/mission/:courseId" element={
        <ProtectedRoute>
          <MissionRunner />
        </ProtectedRoute>
      } />
      <Route path="/exam/:courseId" element={
        <ProtectedRoute>
          <ExamPage />
        </ProtectedRoute>
      } />
      <Route path="/exam-module" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_board_exams">
            <ExamModule />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/paper-scan" element={
        <ProtectedRoute>
          <PaperScanPage />
        </ProtectedRoute>
      } />
      <Route path="/storyteller" element={
        <ProtectedRoute>
          <StorytellerPage />
        </ProtectedRoute>
      } />
      <Route path="/story/:courseId/:chapterIdx" element={
        <ProtectedRoute>
          <StoryTheatrePage />
        </ProtectedRoute>
      } />
      <Route path="/story/:courseId" element={
        <ProtectedRoute>
          <StoryTheatrePage />
        </ProtectedRoute>
      } />
      <Route path="/board-exam-simulator" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_board_exams">
            <BoardExamSimulator />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/competitive-exams" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_competitive_exams">
            <CompetitiveExams />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/error-book" element={
        <ProtectedRoute>
          <ErrorBook />
        </ProtectedRoute>
      } />
      <Route path="/performance-hub" element={
        <ProtectedRoute>
          <PerformanceHub />
        </ProtectedRoute>
      } />
      <Route path="/exam-lab" element={
        <ProtectedRoute>
          <ExamLab />
        </ProtectedRoute>
      } />
      <Route path="/mock-test" element={
        <ProtectedRoute>
          <MockTest />
        </ProtectedRoute>
      } />
      <Route path="/daily-practice" element={
        <ProtectedRoute>
          <DailyPractice />
        </ProtectedRoute>
      } />
      <Route path="/revision" element={
        <ProtectedRoute>
          <RevisionHub />
        </ProtectedRoute>
      } />
      <Route path="/rank-predictor" element={
        <ProtectedRoute>
          <RankPredictor />
        </ProtectedRoute>
      } />
      <Route path="/aits" element={
        <ProtectedRoute>
          <AITSHub />
        </ProtectedRoute>
      } />
      <Route path="/admin/aits" element={
        <ProtectedRoute allowedRoles={['super_admin', 'org_admin', 'admin']}>
          <AdminAITS />
        </ProtectedRoute>
      } />
      <Route path="/study-plan" element={
        <ProtectedRoute>
          <StudyPlan />
        </ProtectedRoute>
      } />
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/gamification" element={
        <ProtectedRoute>
          <Gamification />
        </ProtectedRoute>
      } />
      {/* Classroom Routes */}
      <Route path="/classroom" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <ClassroomHub />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/classroom/:classId" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <ClassDetail />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/classroom/:classId/assignment/:assignmentId" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <AssignmentView />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      {/* Live Classroom Routes */}
      <Route path="/live-classroom" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <LiveClassroom />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/live-classroom/:sessionId" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <LiveClassroom />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      {/* Suite Routes - Hub, Docs, Drive, Calendar, Chat, Email, Meet, Sheets, Slides, Whiteboard */}
      <Route path="/suite" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <SuiteHub />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/docs" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <DocsHub />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/docs/:docId" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <DocEditor />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/drive" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <DrivePage />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <CalendarPage />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/chat" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <ChatPage />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/email" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <EmailPage />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/meet" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <MeetPage />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/meet/:roomId" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <MeetPage />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/sheets" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <SheetsHub />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/sheets/:sheetId" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <SheetEditor />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/slides" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <SlidesHub />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/slides/:presId" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <SlideEditor />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/whiteboard" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <WhiteboardPage />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/whiteboard/:wbId" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_classroom">
            <WhiteboardPage />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/explorer" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_explorer">
            <Explorer />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/bot-exam" element={
        <ProtectedRoute>
          <BotExamDemo />
        </ProtectedRoute>
      } />
      <Route path="/teacher/students" element={
        <ProtectedRoute allowedRoles={['super_admin', 'org_admin', 'school_admin', 'faculty']}>
          <StudentPerformance />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_analytics">
            <AnalyticsDashboard />
          </FlaggedRoute>
        </ProtectedRoute>
      } />

      {/* Visual Labs Routes */}
      <Route path="/labs" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_visual_labs">
            <VisualLabs />
          </FlaggedRoute>
        </ProtectedRoute>
      } />
      <Route path="/lab-progress" element={
        <ProtectedRoute>
          <FlaggedRoute flagId="module_visual_labs">
            <LabProgressPage />
          </FlaggedRoute>
        </ProtectedRoute>
      } />

      {/* Study Material */}
      <Route path="/study-material" element={<ProtectedRoute><StudyMaterialHub /></ProtectedRoute>} />
      <Route path="/study-material/:examId" element={<ProtectedRoute><StudyMaterialSubjects /></ProtectedRoute>} />
      <Route path="/study-material/:examId/:subject" element={<ProtectedRoute><StudyMaterialTopics /></ProtectedRoute>} />
      <Route path="/study-material/:examId/:subject/:topicId" element={<ProtectedRoute><StudyMaterialViewer /></ProtectedRoute>} />

      <Route path="/labs/physics" element={
        <ProtectedRoute>
          <PhysicsLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/chemistry" element={
        <ProtectedRoute>
          <ChemistryLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/sorting" element={
        <ProtectedRoute>
          <SortingLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/drone" element={
        <ProtectedRoute>
          <DroneLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/geometry" element={
        <ProtectedRoute>
          <GeometryLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/ai-prompt" element={
        <ProtectedRoute>
          <AIPromptLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/algebra" element={
        <ProtectedRoute>
          <AlgebraLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/history" element={
        <ProtectedRoute>
          <HistoryLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/geography" element={
        <ProtectedRoute>
          <GeographyLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/electrical" element={
        <ProtectedRoute>
          <ElectricalLab />
        </ProtectedRoute>
      } />
      {/* Game Coding Labs (dynamic) */}
      <Route path="/labs/game/:gameId" element={
        <ProtectedRoute>
          <GameLabRouter />
        </ProtectedRoute>
      } />
      {/* Visual Science/Math Labs (dynamic) */}
      <Route path="/labs/visual/:labId" element={
        <ProtectedRoute>
          <VisualLabRouter />
        </ProtectedRoute>
      } />
      {/* Reusable Interactive Labs */}
      <Route path="/labs/chem-reactions-sim" element={
        <ProtectedRoute>
          <ChemReactionSimLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/virtual-dissection" element={
        <ProtectedRoute>
          <VirtualDissectionLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/physics-sim" element={
        <ProtectedRoute>
          <PhysicsSimLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/earth-explorer" element={
        <ProtectedRoute>
          <EarthExplorerLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/math-visualizer" element={
        <ProtectedRoute>
          <MathVisualizerLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/engineering-sim" element={
        <ProtectedRoute>
          <EngineeringSimLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/engineering-designer" element={
        <ProtectedRoute>
          <Navigate to="/labs/playground" replace />
        </ProtectedRoute>
      } />
      <Route path="/labs/engineering-designer-legacy" element={
        <ProtectedRoute>
          <EngineeringDesignerLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/install/:topic" element={
        <ProtectedRoute>
          <InstallTutorialPage />
        </ProtectedRoute>
      } />
      <Route path="/labs/install" element={
        <ProtectedRoute>
          <InstallTutorialPage />
        </ProtectedRoute>
      } />
      <Route path="/labs/playground" element={
        <ProtectedRoute>
          <EngineeringPlaygroundPage />
        </ProtectedRoute>
      } />
      <Route path="/labs/body-explorer" element={
        <ProtectedRoute>
          <BodyExplorerLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/city-builder" element={
        <ProtectedRoute>
          <CityBuilderLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/finance-sim" element={
        <ProtectedRoute>
          <FinanceSimLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/history-explorer" element={
        <ProtectedRoute>
          <HistoryExplorerLab />
        </ProtectedRoute>
      } />
      <Route path="/labs/:labId" element={
        <ProtectedRoute>
          <InteractiveLab />
        </ProtectedRoute>
      } />

      {/* Parent Dashboard */}
      <Route path="/parent" element={
        <ProtectedRoute allowedRoles={['parent']}>
          <ParentDashboard />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute allowedRoles={['super_admin', 'org_admin', 'faculty']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <FeatureFlagProvider>
            <FloatingLabBack />
            <AppRouter />
            <Toaster position="top-right" richColors />
          </FeatureFlagProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
